import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { db } from "config/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  onSnapshot,
  Query,
  DocumentData,
  Unsubscribe,
} from "firebase/firestore";
import { setPublicCashList, setPublicTourneyList } from "store/actions";
import { Game } from "types";
import type { RootState } from "store";

import * as geofire from "geofire-common";

const useGetPublicGameList = (distance: number = 10): void => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const locationState = useSelector((state: RootState) => state.location);
  const { country, lat, lng } = locationState || {};

  useEffect(() => {
    if (user && country && lat !== null && lng !== null) {
      const listeners: Unsubscribe[] = [];
      let radiusInM = distance * 1000;
      if (["United States", "Liberia", "Myanmar"].includes(country)) {
        const MiPerKm = 0.621371;
        radiusInM *= MiPerKm;
      }
      const center: [number, number] = [lat, lng];
      const bounds = geofire.geohashQueryBounds(center, radiusInM);

      // todo: finish querying with geohash bounds
      const publicCashRefs: Query<DocumentData>[] = [];
      const publicTourneyRefs: Query<DocumentData>[] = [];
      for (const b of bounds) {
        // get public games

        // ? queries separated out by type to reduce number of reads on snapshot change
        const publicCashRef = query(
          collection(db, "games"),
          where("access", "==", "Public"),
          where("type", "==", "Cash"),
          where("visible", "==", true),
          orderBy("geohash"),
          startAt(b[0]),
          endAt(b[1]),
        );

        const publicTourneyRef = query(
          collection(db, "games"),
          where("access", "==", "Public"),
          where("type", "==", "Tournament"),
          where("visible", "==", true),
          orderBy("geohash"),
          startAt(b[0]),
          endAt(b[1]),
        );

        publicCashRefs.push(publicCashRef);
        publicTourneyRefs.push(publicTourneyRef);
      }

      // Create maps to store data from each geohash bound
      const cashGamesByBound = new Map<number, Record<string, Game>>();
      const tourneyGamesByBound = new Map<number, Record<string, Game>>();

      // Function to merge all bounds data and dispatch
      const updateCashGames = () => {
        const allCashGames: Record<string, Game> = {};
        cashGamesByBound.forEach((boundGames) => {
          Object.assign(allCashGames, boundGames);
        });
        dispatch(setPublicCashList(allCashGames));
      };

      const updateTourneyGames = () => {
        const allTourneyGames: Record<string, Game> = {};
        tourneyGamesByBound.forEach((boundGames) => {
          Object.assign(allTourneyGames, boundGames);
        });
        dispatch(setPublicTourneyList(allTourneyGames));
      };

      publicCashRefs.forEach((ref, index) => {
        const unsubscribe = onSnapshot(
          ref,
          async (res) => {
            const boundCashGames: Record<string, Game> = {};
            res.docs.forEach((doc) => {
              const data = doc.data() as Game;
              const { id } = data;
              boundCashGames[id] = { ...data };
            });
            
            // Store this bound's data
            cashGamesByBound.set(index, boundCashGames);
            
            // Update the combined list
            updateCashGames();
          },
          (err) => {
            console.log(
              `Encountered error while getting public cash list: ${err}`,
            );
          },
        );
        listeners.push(unsubscribe);
      });

      publicTourneyRefs.forEach((ref, index) => {
        const unsubscribe = onSnapshot(
          ref,
          async (res) => {
            const boundTourneyGames: Record<string, Game> = {};
            res.docs.forEach((doc) => {
              const data = doc.data() as Game;
              const { id } = data;
              boundTourneyGames[id] = { ...data };
            });
            
            // Store this bound's data
            tourneyGamesByBound.set(index, boundTourneyGames);
            
            // Update the combined list
            updateTourneyGames();
          },
          (err) => {
            console.log(
              `Encountered error while getting public tourney list: ${err}`,
            );
          },
        );
        listeners.push(unsubscribe);
      });

      return () => {
        listeners.forEach((fn) => fn());
        // Clear the maps to prevent memory leaks
        cashGamesByBound.clear();
        tourneyGamesByBound.clear();
      };
    } else {
      // If prerequisites not met, set empty game lists to stop loading indicators
      dispatch(setPublicCashList({}));
      dispatch(setPublicTourneyList({}));
    }
    // If user/location is not available, don't set up listeners
    return undefined;
  }, [user, country, lat, lng, distance, dispatch]);
};

export default useGetPublicGameList;
